import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, ExternalLink, ArrowRight, Sparkles, BookOpen, Shield, Users, Lightbulb, Handshake, Palette } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const valueIcons: Record<string, React.ElementType> = {
  'Faith': Sparkles,
  'Humility': Heart,
  'Fear of The Lord': BookOpen,
  'Honesty': Shield,
  'Excellence': Lightbulb,
  'Collaboration': Handshake,
  'Creativity': Palette
};

export const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      const response = await fetch('https://hook.us2.make.com/2t9a6thh3kcu98eedpd6752imtx4egnb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'email_subscription',
          email,
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        toast.success('Successfully subscribed to updates!');
        setEmail('');
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const companyValues = [
    { title: 'Faith', verse: 'Matthew 21:22', description: 'Believing in what we cannot see' },
    { title: 'Humility', verse: 'James 4:10', description: 'Serving others before ourselves' },
    { title: 'Fear of The Lord', verse: 'Proverbs 9:10', description: 'The beginning of wisdom' },
    { title: 'Honesty', verse: 'Proverbs 11:3', description: 'Integrity guides our path' },
    { title: 'Excellence', verse: 'Colossians 3:23', description: 'Working as unto the Lord' },
    { title: 'Collaboration', verse: '1 Corinthians 3:9', description: 'Building together in unity' },
    { title: 'Creativity', verse: 'Genesis 1:1', description: 'Made in the Creator\'s image' }
  ];

  const footerLinks = {
    platform: [
      { label: 'Discover', href: '/discover' },
      { label: 'Community', href: '/community' },
      { label: 'Rewards', href: '/rewards' },
      { label: 'Library', href: '/library' },
    ],
    artists: [
      { label: 'Vision Studio', href: '/creator-portal' },
      { label: 'Upload Music', href: '/artist/upload' },
      { label: 'Submit Music', href: '/submit-music' },
      { label: 'Become an Artist', href: '/become-artist' },
    ],
    label: [
      { label: 'Our Artists', href: '/artists' },
      { label: 'Partnerships', href: '/partners' },
      { label: 'Production', href: 'https://VISIONStudios.io', external: true },
    ],
    support: [
      { label: 'About Us', href: '/about' },
      { label: 'Help Center', href: '/help' },
      { label: 'Contact', href: '/contact' },
    ],
  };

  return (
    <footer className="relative mt-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-stone-50/50 to-stone-100/80" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] bg-royal-gold/[0.03] rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] bg-royal-gold-light/[0.04] rounded-full blur-[100px]" />
      </div>

      <div className="relative">
        <div className="py-20 lg:py-28">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-16"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-royal-gold/5 border border-royal-gold/10 rounded-full text-sm font-medium text-royal-gold mb-6">
                <Sparkles className="w-4 h-4" />
                Built on Biblical Principles
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-royal-text mb-4 tracking-tight">
                Our Values
              </h2>
              <p className="text-lg text-royal-text-secondary max-w-2xl mx-auto leading-relaxed">
                Every decision we make is guided by these seven pillars of faith
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 lg:gap-3">
              {companyValues.map((value, index) => {
                const Icon = valueIcons[value.title] || Sparkles;
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="group relative"
                  >
                    <div className="relative h-full bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-5 lg:p-4 transition-all duration-500 hover:bg-white hover:border-royal-gold/20 hover:shadow-lg hover:shadow-royal-gold/5 hover:-translate-y-1">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-royal-gold/10 to-royal-gold-light/10 flex items-center justify-center mb-4 group-hover:from-royal-gold/20 group-hover:to-royal-gold-light/20 transition-all duration-500">
                        <Icon className="w-5 h-5 text-royal-gold" />
                      </div>
                      <h3 className="font-semibold text-royal-text mb-1 text-sm lg:text-base">{value.title}</h3>
                      <p className="text-xs text-royal-gold font-medium mb-2">{value.verse}</p>
                      <p className="text-xs text-royal-text-secondary leading-relaxed hidden lg:block">{value.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-stone-200/60 py-20">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-2xl mx-auto text-center"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-royal-text mb-4 tracking-tight">
                Stay in the Loop
              </h2>
              <p className="text-royal-text-secondary mb-8 text-lg">
                Get exclusive updates on new releases, artist news, and early access to features.
              </p>

              <form onSubmit={handleSubscribe} className="relative">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-royal-text-secondary" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full bg-white border border-stone-200 rounded-xl pl-12 pr-4 py-4 text-royal-text placeholder:text-royal-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-royal-gold/20 focus:border-royal-gold/40 transition-all text-base"
                      required
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={subscribing}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-royal-gold text-white rounded-xl font-semibold hover:bg-royal-gold-dark transition-all duration-300 disabled:opacity-50 shadow-lg shadow-royal-gold/20"
                  >
                    {subscribing ? (
                      <span>Subscribing...</span>
                    ) : (
                      <>
                        Subscribe
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </div>
                <p className="text-xs text-royal-text-secondary mt-4">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            </motion.div>
          </div>
        </div>

        <div className="border-t border-stone-200/60 py-16">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
              <div className="col-span-2 lg:col-span-2">
                <Link to="/" className="inline-block mb-5">
                  <img
                    src="https://ighpjdpsntdcsdvqnipc.supabase.co/storage/v1/object/public/Clarity%20Vision/Clarity%20Vision%20Logo%20Glow.PNG"
                    alt="Vision Music"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <p className="text-royal-text-secondary text-sm leading-relaxed max-w-xs mb-6">
                  Christian music label and streaming platform dedicated to advancing God's Kingdom through music.
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://instagram.com/visionmusic"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-stone-100 hover:bg-royal-gold/10 flex items-center justify-center transition-colors"
                  >
                    <svg className="w-5 h-5 text-royal-text-secondary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    href="https://twitter.com/visionmusic"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-stone-100 hover:bg-royal-gold/10 flex items-center justify-center transition-colors"
                  >
                    <svg className="w-5 h-5 text-royal-text-secondary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a
                    href="https://youtube.com/@visionmusic"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-stone-100 hover:bg-royal-gold/10 flex items-center justify-center transition-colors"
                  >
                    <svg className="w-5 h-5 text-royal-text-secondary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-royal-text mb-4 text-sm">Platform</h4>
                <ul className="space-y-3">
                  {footerLinks.platform.map(link => (
                    <li key={link.label}>
                      <Link to={link.href} className="text-sm text-royal-text-secondary hover:text-royal-gold transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-royal-gold mb-4 text-sm">For Artists</h4>
                <ul className="space-y-3">
                  {footerLinks.artists.map(link => (
                    <li key={link.label}>
                      <Link to={link.href} className="text-sm text-royal-text-secondary hover:text-royal-gold transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-royal-text mb-4 text-sm">Label</h4>
                <ul className="space-y-3">
                  {footerLinks.label.map(link => (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-royal-text-secondary hover:text-royal-gold transition-colors"
                        >
                          {link.label}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <Link to={link.href} className="text-sm text-royal-text-secondary hover:text-royal-gold transition-colors">
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-royal-text mb-4 text-sm">Support</h4>
                <ul className="space-y-3">
                  {footerLinks.support.map(link => (
                    <li key={link.label}>
                      <Link to={link.href} className="text-sm text-royal-text-secondary hover:text-royal-gold transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-200/60 py-8">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm text-royal-text-secondary">
                <div className="flex items-center gap-2">
                  <span>Made with</span>
                  <Heart className="w-4 h-4 text-red-500" fill="currentColor" />
                  <span>by</span>
                  <a
                    href="https://VISIONStudios.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-royal-gold hover:text-royal-gold-dark transition-colors font-medium"
                  >
                    VISION Studios
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-sm text-royal-text-secondary">
                <span>&copy; {currentYear} Vision Music</span>
                <div className="flex items-center gap-4">
                  <Link to="/privacy" className="hover:text-royal-gold transition-colors">Privacy</Link>
                  <Link to="/terms" className="hover:text-royal-gold transition-colors">Terms</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
